import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import supabase from '@/supabaseClient'
import { logFeatureUsage } from '@/utils/logger'

type ShuffleGame = {
  id: string
  title: string
  status: string
  progress: number
  updatedAt: string
  image?: string
  reason: string
}

type UserGameRow = {
  id: string
  status: string
  progress: number | null
  updated_at: string
  game: {
    id: string
    title: string
    background_image?: string
  } | null
}

const EXCLUDED_STATUSES = ['Done', 'Endless', 'Satisfied', 'DNF']
const RANDOM_POOL_SIZE = 6

const STACK_TRANSFORMS = [
  { transform: 'translate(0px, 0px) rotate(0deg) scale(1)', zIndex: 40 },
  { transform: 'translate(10px, 8px) rotate(3deg) scale(0.97)', zIndex: 30 },
  { transform: 'translate(-8px, 14px) rotate(-2deg) scale(0.94)', zIndex: 20 },
  { transform: 'translate(5px, 20px) rotate(1deg) scale(0.91)', zIndex: 10 },
]

const SerendipityShuffle = ({ isDevUser }: { isDevUser: boolean }) => {
  const navigate = useNavigate()
  const [shuffledGames, setShuffledGames] = useState<ShuffleGame[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [flyingIndex, setFlyingIndex] = useState<number | null>(null)

  const dealNext = () => {
    if (flyingIndex !== null || shuffledGames.length <= 1) return
    setFlyingIndex(activeIndex)
    setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % shuffledGames.length)
      setFlyingIndex(null)
    }, 320)
  }

  const shuffle = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser()

      if (userError || !user) {
        setError('Sign in to shuffle your backlog.')
        return
      }

      const { data, error: gamesError } = await supabase
        .from('user_games')
        .select(
          `
          id,
          status,
          progress,
          updated_at,
          game:games!user_games_game_id_fkey (
            id,
            title,
            background_image
          )
        `
        )
        .eq('user_id', user.id)
        .not('status', `in.("${EXCLUDED_STATUSES.join('","')}")`)

      if (gamesError) {
        console.error('Error fetching user games:', gamesError.message)
        setError('Could not load your backlog. Give it another try.')
        return
      }

      const backlog = (data as UserGameRow[] | null)
        ?.filter((entry) => entry.game)
        .map((entry) => ({
          id: entry.game?.id || entry.id,
          title: entry.game?.title || 'Unknown Title',
          status: entry.status,
          progress: entry.progress ?? 0,
          updatedAt: entry.updated_at,
          image: entry.game?.background_image || undefined,
        }))

      if (!backlog || backlog.length === 0) {
        setError('Add some games to your library first — then we have something to shuffle.')
        return
      }

      const remaining = [...backlog]
      const picks: ShuffleGame[] = []

      const takeByIndex = (index: number, reason: string) => {
        const [game] = remaining.splice(index, 1)
        if (!game) return
        picks.push({ ...game, reason })
      }

      const takeRandomFromSorted = (
        sorter: (a: ShuffleGame, b: ShuffleGame) => number,
        reasonBuilder: (game: ShuffleGame) => string
      ) => {
        const sorted = [...remaining].sort(sorter)
        const pool = sorted.slice(0, Math.min(RANDOM_POOL_SIZE, sorted.length))
        if (pool.length === 0) return
        const chosen = pool[Math.floor(Math.random() * pool.length)]
        const chosenIndex = remaining.findIndex((item) => item.id === chosen.id)
        if (chosenIndex === -1) return
        takeByIndex(chosenIndex, reasonBuilder(chosen))
      }

      const updatedAtValue = (game: ShuffleGame) => {
        const parsed = Date.parse(game.updatedAt || '')
        return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed
      }

      const pickOldest = () => {
        takeRandomFromSorted(
          (a, b) => updatedAtValue(a) - updatedAtValue(b),
          () => 'You keep scrolling past this one.'
        )
      }

      const pickUnderplayed = () => {
        takeRandomFromSorted(
          (a, b) => a.progress - b.progress,
          (game) =>
            game.progress === 0
              ? 'You added this and never started it.'
              : 'You barely got past the opening.'
        )
      }

      const pickNearlyThere = () => {
        takeRandomFromSorted(
          (a, b) => b.progress - a.progress,
          (game) =>
            game.progress >= 80
              ? "You're so close. Just finish it."
              : 'The ending is right there.'
        )
      }

      const pickRandom = () => {
        const index = Math.floor(Math.random() * remaining.length)
        takeByIndex(index, 'No reason. Just vibes.')
      }

      pickOldest()
      if (remaining.length > 0) pickUnderplayed()
      if (remaining.length > 0) pickNearlyThere()
      if (remaining.length > 0) pickRandom()

      logFeatureUsage({
        feature: 'serendipity_shuffle',
        metadata: {
          backlogSize: backlog.length,
          pickCount: picks.length,
          isDevUser,
        },
      })

      setShuffledGames(picks)
      setActiveIndex(0)
      setFlyingIndex(null)
    } catch (e) {
      console.error('Serendipity shuffle failed:', e)
      setError('Something went wrong. Give it another try.')
    } finally {
      setIsLoading(false)
    }
  }

  const markCurrentlyPlaying = async (gameId: string) => {
    setUpdatingId(gameId)
    setError(null)

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser()

      if (userError || !user) {
        setError('Sign in to update your backlog.')
        return
      }

      const { error: updateError } = await supabase
        .from('user_games')
        .update({ status: 'Currently Playing', updated_at: new Date().toISOString() })
        .eq('game_id', gameId)
        .eq('user_id', user.id)

      if (updateError) {
        console.error('Error updating status:', updateError.message)
        setError('Could not update status. Give it another try.')
        return
      }

      setShuffledGames((prev) =>
        prev.map((game) =>
          game.id === gameId ? { ...game, status: 'Currently Playing' } : game
        )
      )
    } catch (e) {
      console.error('Mark currently playing failed:', e)
      setError('Something went wrong. Give it another try.')
    } finally {
      setUpdatingId(null)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: '#c4714a' }}>
        Shuffle
      </p>
      <h1 className="text-2xl font-bold font-serif italic mb-2" style={{ color: '#3d2a1e' }}>
        Nothing feeling right tonight?
      </h1>
      <p className="text-sm mb-8" style={{ color: '#a68565' }}>
        No AI, no algorithm. Just a playful mix of ignored, underplayed, and wildcard picks from your library. Something in there is worth playing tonight.
      </p>

      <button
        className="btn btn-primary rounded-full px-8 mb-8"
        onClick={shuffle}
        disabled={isLoading}
      >
        {isLoading ? 'Shuffling…' : 'Shuffle ✦'}
      </button>

      {error && <p className="text-sm text-error mb-6">{error}</p>}

      {shuffledGames.length > 0 && (
        <div>
          <div className="relative flex justify-center" style={{ height: '400px' }}>
            {shuffledGames.map((game, index) => {
              const pos = (index - activeIndex + shuffledGames.length) % shuffledGames.length
              const isFlyingOut = flyingIndex === index
              const stackStyle = STACK_TRANSFORMS[Math.min(pos, STACK_TRANSFORMS.length - 1)]

              return (
                <div
                  key={game.id}
                  className="shuffle-card absolute"
                  style={{
                    left: '50%',
                    marginLeft: '-10rem',
                    zIndex: stackStyle.zIndex,
                    animationDelay: `${index * 100}ms`,
                  }}
                >
                  <div
                    className="w-80 rounded-2xl overflow-hidden cursor-pointer"
                    style={{
                      transform: isFlyingOut
                        ? 'translate(-420px, -30px) rotate(-15deg)'
                        : stackStyle.transform,
                      opacity: isFlyingOut ? 0 : 1,
                      transition: isFlyingOut
                        ? 'transform 0.3s ease-in, opacity 0.25s ease-in'
                        : 'transform 0.35s ease-out',
                      border: '1px solid #ecd5b8',
                      boxShadow: pos === 0
                        ? '0 8px 32px rgba(0,0,0,0.13)'
                        : '0 2px 8px rgba(0,0,0,0.06)',
                    }}
                    onClick={dealNext}
                  >
                    {game.image ? (
                      <img
                        src={game.image}
                        alt={game.title}
                        className="w-full aspect-video object-cover"
                      />
                    ) : (
                      <div className="w-full aspect-video" style={{ background: '#f5e6d0' }} />
                    )}
                    <div className="p-4 flex flex-col gap-3" style={{ background: '#fdf6ee' }}>
                      <div>
                        <p className="font-semibold text-sm leading-snug" style={{ color: '#3d2a1e' }}>
                          {game.title}
                        </p>
                        <p className="text-xs mt-1" style={{ color: '#a68565' }}>{game.reason}</p>
                      </div>
                      <button
                        className="btn btn-xs btn-primary rounded-full w-full"
                        onClick={(e) => {
                          e.stopPropagation()
                          markCurrentlyPlaying(game.id)
                        }}
                        disabled={updatingId === game.id || game.status === 'Currently Playing'}
                      >
                        {game.status === 'Currently Playing'
                          ? 'playing'
                          : updatingId === game.id
                          ? '…'
                          : 'play this'}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="flex justify-center mt-4">
            <p className="text-xs" style={{ color: '#a68565' }}>
              {activeIndex + 1} / {shuffledGames.length} — tap to deal
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default SerendipityShuffle
